import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  providerService,
  type Provider,
  type ProviderList,
  type ProviderSearchRequest,
  type ProviderSearchResponse,
  type CreateProviderRequest,
  type UpdateProviderRequest,
  type UpdateProviderStatusRequest,
  type Specialization,
  type ProviderStats,
  ProviderStatus,
  ProviderType
} from '@/services/providerService'
import { SubscriptionService, type UsageSummaryDto } from '@/services/subscriptionService'
import { isSessionExpiredError } from '@/services/api'

export const useProviderStore = defineStore('provider', () => {
  // State
  const providers = ref<ProviderList[]>([])
  const currentProvider = ref<Provider | null>(null)
  const myProvider = ref<Provider | null>(null)
  const specializations = ref<Specialization[]>([])
  const providerStats = ref<ProviderStats | null>(null)
  const topRatedProviders = ref<ProviderList[]>([])
  const subscriptionUsage = ref<UsageSummaryDto | null>(null)
  const loadingSubscriptionUsage = ref(false)
  
  // Pagination and filtering state
  const currentPage = ref(1)
  const pageSize = ref(10)
  const totalCount = ref(0)
  const totalPages = ref(0)
  const searchTerm = ref('')
  const selectedStatus = ref<ProviderStatus | undefined>(undefined)
  const selectedType = ref<ProviderType | undefined>(undefined)
  const selectedSpecialization = ref<number | undefined>(undefined)
  const isVerifiedFilter = ref<boolean | undefined>(undefined)
  const isActiveFilter = ref<boolean | undefined>(undefined)
  const minRatingFilter = ref<number | undefined>(undefined)
  const sortBy = ref('CreatedAt')
  const sortDirection = ref('desc')
  
  // Loading states
  const loading = ref(false)
  const loadingProvider = ref(false)
  const loadingMyProvider = ref(false)
  const loadingStats = ref(false)
  const loadingSpecializations = ref(false)

  // Computed
  const totalPagesComputed = computed(() => Math.ceil(totalCount.value / pageSize.value))
  
  const hasProviders = computed(() => providers.value.length > 0)
  
  const filteredProviders = computed(() => {
    let filtered = providers.value
    
    if (searchTerm.value) {
      const search = searchTerm.value.toLowerCase()
      filtered = filtered.filter(provider => 
        provider.businessName.toLowerCase().includes(search) ||
        provider.userName.toLowerCase().includes(search) ||
        provider.userEmail.toLowerCase().includes(search) ||
        (provider.businessDescription && provider.businessDescription.toLowerCase().includes(search))
      )
    }
    
    return filtered
  })

  const activeSpecializations = computed(() => 
    specializations.value.filter(spec => spec.isActive)
  )

  // Actions
  async function fetchProviders(params: ProviderSearchRequest = {}) {
    loading.value = true
    try {
      const searchParams: ProviderSearchRequest = {
        page: currentPage.value,
        pageSize: pageSize.value,
        searchTerm: searchTerm.value || undefined,
        status: selectedStatus.value,
        type: selectedType.value,
        isVerified: isVerifiedFilter.value,
        isActive: isActiveFilter.value,
        specializationId: selectedSpecialization.value !== undefined ? String(selectedSpecialization.value) : undefined,
        minRating: minRatingFilter.value,
        sortBy: sortBy.value,
        sortDirection: sortDirection.value,
        ...params
      }

      const response: ProviderSearchResponse = await providerService.getProviders(searchParams)
      providers.value = response.providers
      totalCount.value = response.totalCount
      totalPages.value = response.totalPages
      currentPage.value = response.page

      // Update specializations if available
      if (response.specializations.length > 0) {
        specializations.value = response.specializations
      }
    } catch (error) {
      // Don't log session expired errors - already handled by API interceptor
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching providers:', error)
      }
      throw error
    } finally {
      loading.value = false
    }
  }

  async function fetchProvider(id: string) {
    loadingProvider.value = true
    try {
      const provider = await providerService.getProvider(id)
      currentProvider.value = provider
      return provider
    } catch (error) {
      // Don't log session expired errors - already handled by API interceptor
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching provider:', error)
      }
      throw error
    } finally {
      loadingProvider.value = false
    }
  }

  async function fetchMyProvider() {
    loadingMyProvider.value = true
    try {
      const provider = await providerService.getMyProvider()
      myProvider.value = provider
      return provider
    } catch (error) {
      // Don't log session expired errors - already handled by API interceptor
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching my provider:', error)
      }
      myProvider.value = null
      throw error
    } finally {
      loadingMyProvider.value = false
    }
  }

  async function createProvider(data: CreateProviderRequest) {
    loading.value = true
    try {
      const provider = await providerService.createProvider(data)
      myProvider.value = provider
      await fetchProviders() // Refresh the list
      return provider
    } catch (error) {
      console.error('Error creating provider:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function updateProvider(id: string, data: UpdateProviderRequest) {
    loading.value = true
    try {
      const provider = await providerService.updateProvider(id, data)
      
      // Update the provider in the list
      const index = providers.value.findIndex(p => p.id === id)
      if (index !== -1) {
        // Update basic fields that exist in ProviderList
        providers.value[index] = {
          ...providers.value[index],
          businessName: provider.businessName,
          businessDescription: provider.businessDescription
        }
      }
      
      // Update current provider if it's the same
      if (currentProvider.value?.id === id) {
        currentProvider.value = provider
      }
      
      // Update my provider if it's the same
      if (myProvider.value?.id === id) {
        myProvider.value = provider
      }
      
      return provider
    } catch (error) {
      console.error('Error updating provider:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function updateProviderStatus(id: string, data: UpdateProviderStatusRequest) {
    loading.value = true
    try {
      const provider = await providerService.updateProviderStatus(id, data)
      
      // Update the provider in the list
      const index = providers.value.findIndex(p => p.id === id)
      if (index !== -1) {
        providers.value[index] = {
          ...providers.value[index],
          status: provider.status,
          isVerified: provider.isVerified
        }
      }
      
      // Update current provider if it's the same
      if (currentProvider.value?.id === id) {
        currentProvider.value = provider
      }
      
      return provider
    } catch (error) {
      console.error('Error updating provider status:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function fetchSpecializations() {
    loadingSpecializations.value = true
    try {
      const specs = await providerService.getSpecializations()
      specializations.value = specs
      return specs
    } catch (error) {
      console.error('Error fetching specializations:', error)
      throw error
    } finally {
      loadingSpecializations.value = false
    }
  }

  async function fetchProviderStats() {
    loadingStats.value = true
    try {
      const stats = await providerService.getProviderStats()
      providerStats.value = stats
      return stats
    } catch (error) {
      console.error('Error fetching provider stats:', error)
      throw error
    } finally {
      loadingStats.value = false
    }
  }

  async function fetchTopRatedProviders(count: number = 10) {
    loading.value = true
    try {
      const providers = await providerService.getTopRatedProviders(count)
      topRatedProviders.value = providers
      return providers
    } catch (error) {
      console.error('Error fetching top rated providers:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function fetchSubscriptionUsage() {
    loadingSubscriptionUsage.value = true
    try {
      const usage = await SubscriptionService.getUsageSummary()
      subscriptionUsage.value = usage
      return usage
    } catch (error) {
      // Don't log session expired errors - already handled by API interceptor
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching subscription usage:', error)
      }
      subscriptionUsage.value = null
      throw error
    } finally {
      loadingSubscriptionUsage.value = false
    }
  }

  // Computed properties for subscription limits
  const canAddService = computed(() => subscriptionUsage.value?.canAddService ?? true)
  const canAddLocation = computed(() => subscriptionUsage.value?.canAddLocation ?? true)
  const serviceLimitReached = computed(() => subscriptionUsage.value?.serviceLimitReached ?? false)
  const locationLimitReached = computed(() => subscriptionUsage.value?.locationLimitReached ?? false)
  const serviceUsageText = computed(() => {
    if (!subscriptionUsage.value) return ''
    const limit = subscriptionUsage.value.serviceLimit
    if (limit === null || limit === -1) return `${subscriptionUsage.value.serviceCount} services (Unlimited)`
    return `${subscriptionUsage.value.serviceCount}/${limit} services`
  })
  const locationUsageText = computed(() => {
    if (!subscriptionUsage.value) return ''
    const limit = subscriptionUsage.value.locationLimit
    if (limit === null || limit === -1) return `${subscriptionUsage.value.locationCount} locations (Unlimited)`
    return `${subscriptionUsage.value.locationCount}/${limit} locations`
  })

  // Pagination functions
  function setPage(page: number) {
    currentPage.value = page
    fetchProviders()
  }

  function setPageSize(size: number) {
    pageSize.value = size
    currentPage.value = 1
    fetchProviders()
  }

  function nextPage() {
    if (currentPage.value < totalPages.value) {
      setPage(currentPage.value + 1)
    }
  }

  function prevPage() {
    if (currentPage.value > 1) {
      setPage(currentPage.value - 1)
    }
  }

  // Filter functions
  function setSearchTerm(term: string) {
    searchTerm.value = term
    currentPage.value = 1
    fetchProviders()
  }

  function setStatusFilter(status: ProviderStatus | undefined) {
    selectedStatus.value = status
    currentPage.value = 1
    fetchProviders()
  }

  function setTypeFilter(type: ProviderType | undefined) {
    selectedType.value = type
    currentPage.value = 1
    fetchProviders()
  }

  function setSpecializationFilter(specializationId: number | undefined) {
    selectedSpecialization.value = specializationId
    currentPage.value = 1
    fetchProviders()
  }

  function setVerifiedFilter(isVerified: boolean | undefined) {
    isVerifiedFilter.value = isVerified
    currentPage.value = 1
    fetchProviders()
  }

  function setActiveFilter(isActive: boolean | undefined) {
    isActiveFilter.value = isActive
    currentPage.value = 1
    fetchProviders()
  }

  function setMinRatingFilter(minRating: number | undefined) {
    minRatingFilter.value = minRating
    currentPage.value = 1
    fetchProviders()
  }

  function setSorting(field: string, direction: string = 'desc') {
    sortBy.value = field
    sortDirection.value = direction
    fetchProviders()
  }

  function clearFilters() {
    searchTerm.value = ''
    selectedStatus.value = undefined
    selectedType.value = undefined
    selectedSpecialization.value = undefined
    isVerifiedFilter.value = undefined
    isActiveFilter.value = undefined
    minRatingFilter.value = undefined
    sortBy.value = 'CreatedAt'
    sortDirection.value = 'desc'
    currentPage.value = 1
    fetchProviders()
  }

  function resetStore() {
    providers.value = []
    currentProvider.value = null
    myProvider.value = null
    specializations.value = []
    providerStats.value = null
    topRatedProviders.value = []
    currentPage.value = 1
    totalCount.value = 0
    totalPages.value = 0
    clearFilters()
  }

  // Document Management
  async function uploadProviderDocument(_providerId: string, _documentData: FormData) {
    loading.value = true
    try {
      // In a real app, this would call the API
      // const document = await providerService.uploadDocument(providerId, documentData)
      
      // Mock response
      const document = {
        id: Date.now(),
        type: 0, // Will be set from form data
        name: 'Document',
        description: '',
        fileUrl: 'https://example.com/document.pdf',
        status: 0, // Pending
        createdAt: new Date().toISOString()
      }
      
      return document
    } catch (error) {
      console.error('Error uploading document:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function deleteProviderDocument(_providerId: string, _documentId: string) {
    loading.value = true
    try {
      // In a real app, this would call the API
      // await providerService.deleteDocument(providerId, documentId)
      
      return true
    } catch (error) {
      console.error('Error deleting document:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Specialization Management
  async function addProviderSpecialization(providerId: string, specializationData: any) {
    loading.value = true
    try {
      // In a real app, this would call the API
      // const specialization = await providerService.addSpecialization(providerId, specializationData)
      
      // Update current provider if it's loaded
      if (myProvider.value?.id === providerId) {
        if (!myProvider.value.specializations) {
          myProvider.value.specializations = []
        }
        myProvider.value.specializations.push(specializationData)
      }
      
      return specializationData
    } catch (error) {
      console.error('Error adding specialization:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function updateProviderSpecialization(providerId: string, specializationId: string, specializationData: any) {
    loading.value = true
    try {
      // In a real app, this would call the API
      // const specialization = await providerService.updateSpecialization(providerId, specializationId, specializationData)
      
      // Update current provider if it's loaded
      if (myProvider.value?.id === providerId && myProvider.value.specializations) {
        const index = myProvider.value.specializations.findIndex(s => s.id === specializationId)
        if (index !== -1) {
          myProvider.value.specializations[index] = specializationData
        }
      }
      
      return specializationData
    } catch (error) {
      console.error('Error updating specialization:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function removeProviderSpecialization(providerId: string, specializationId: string) {
    loading.value = true
    try {
      // In a real app, this would call the API
      // await providerService.removeSpecialization(providerId, specializationId)
      
      // Update current provider if it's loaded
      if (myProvider.value?.id === providerId && myProvider.value.specializations) {
        myProvider.value.specializations = myProvider.value.specializations.filter(s => s.id !== specializationId)
      }
      
      return true
    } catch (error) {
      console.error('Error removing specialization:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Availability Management
  async function getProviderAvailability(_providerId: string) {
    loading.value = true
    try {
      // In a real app, this would call the API
      // const availability = await providerService.getAvailability(providerId)

      // Mock data
      const availability: { id: string; dayOfWeek: number; startTime: string; endTime: string }[] = []

      return availability
    } catch (error) {
      console.error('Error getting availability:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function saveProviderAvailability(_providerId: string, availabilityData: any) {
    loading.value = true
    try {
      // In a real app, this would call the API
      // const availability = await providerService.saveAvailability(providerId, availabilityData)
      
      return availabilityData
    } catch (error) {
      console.error('Error saving availability:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function deleteProviderAvailability(_providerId: string, _availabilityId: string) {
    loading.value = true
    try {
      // In a real app, this would call the API
      // await providerService.deleteAvailability(providerId, availabilityId)
      
      return true
    } catch (error) {
      console.error('Error deleting availability:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Profile Picture Management
  async function updateProviderProfilePicture(providerId: string, _imageFile: File) {
    loading.value = true
    try {
      // Note: uploadProviderProfileImage method not available in providerService
      // This would need to be implemented in the service when the API supports it
      // For now, return a placeholder
      const imageUrl = ''

      // Note: Provider interface doesn't have profileImageUrl property
      // The update would need to be handled differently when implemented
      console.log('Profile picture upload for provider:', providerId)

      return imageUrl
    } catch (error) {
      console.error('Error updating profile picture:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    providers,
    currentProvider,
    myProvider,
    specializations,
    providerStats,
    topRatedProviders,
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    searchTerm,
    selectedStatus,
    selectedType,
    selectedSpecialization,
    isVerifiedFilter,
    isActiveFilter,
    minRatingFilter,
    sortBy,
    sortDirection,
    loading,
    loadingProvider,
    loadingMyProvider,
    loadingStats,
    loadingSpecializations,

    // Computed
    totalPagesComputed,
    hasProviders,
    filteredProviders,
    activeSpecializations,

    // Actions
    fetchProviders,
    fetchProvider,
    fetchMyProvider,
    createProvider,
    updateProvider,
    updateProviderStatus,
    fetchSpecializations,
    fetchProviderStats,
    fetchTopRatedProviders,
    setPage,
    setPageSize,
    nextPage,
    prevPage,
    setSearchTerm,
    setStatusFilter,
    setTypeFilter,
    setSpecializationFilter,
    setVerifiedFilter,
    setActiveFilter,
    setMinRatingFilter,
    setSorting,
    clearFilters,
    resetStore,
    
    // Document Management
    uploadProviderDocument,
    deleteProviderDocument,
    
    // Specialization Management
    addProviderSpecialization,
    updateProviderSpecialization,
    removeProviderSpecialization,
    
    // Availability Management
    getProviderAvailability,
    saveProviderAvailability,
    deleteProviderAvailability,
    
    // Profile Management
    updateProviderProfilePicture,

    // Subscription Usage
    subscriptionUsage,
    loadingSubscriptionUsage,
    fetchSubscriptionUsage,
    canAddService,
    canAddLocation,
    serviceLimitReached,
    locationLimitReached,
    serviceUsageText,
    locationUsageText
  }
})